import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import Toast from 'components/Toast';
import KanbanCardItem from './KanbanCardItem';
import SortableListWrapper from './SortableListWrapper';
import CardDetailModal from './CardDetailModal';
import ArchivePanel from './ArchivePanel';
import BoardMembersPanel from './BoardMembersPanel';
import ListMenu from './ListMenu';
import CardMenu from './CardMenu';
import KanbanConfirmModal from './KanbanConfirmModal';
import BoardPicker from './BoardPicker';
import KanbanBoardHeader from './KanbanBoardHeader';
import BoardBackgroundModal from './BoardBackgroundModal';
import BoardTextColorModal from './BoardTextColorModal';
import { getBoardBackgroundDownloadUrl } from 'services/kanban';
import { useKanbanBoard } from './useKanbanBoard';
import styles from './Kanban.module.scss';

function Kanban() {
  const board = useKanbanBoard();

  const {
    companyId,
    activeBoardId,
    activeBoard,
    setActiveBoardId,
    boards,
    boardsLoading,
    boardsError,
    creatingBoard,
    boardDataError,
    loadBoardData,
    columns,
    cards,
    boardLabels,
    boardMembers,
    editingBoardTitle,
    setEditingBoardTitle,
    boardTitleDraft,
    setBoardTitleDraft,
    handleBoardTitleClick,
    handleBoardTitleSubmit,
    boardMenuButtonRef,
    boardMenuOpen,
    setBoardMenuOpen,
    archiveCount,
    boardWrapperRef,
    sensors,
    collisionDetection,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    handleBoardMouseDown,
    handleBoardMouseMove,
    handleBoardMouseUp,
    handleBoardMouseLeave,
    addingColumn,
    newColumnTitle,
    setNewColumnTitle,
    handleAddColumnClick,
    handleAddColumnConfirm,
    handleAddColumnCancel,
    activeCard,
    activeListColumn,
    selectedCardId,
    setSelectedCardId,
    commentIdToScroll,
    onCopyCommentLink,
    onCopyCardLink,
    selectedCard,
    selectedColumnTitle,
    setRenameCardId,
    renameCardId,
    editingColumnId,
    setEditingColumnId,
    editingColumnTitle,
    setEditingColumnTitle,
    editingCardTitleId,
    setEditingCardTitleId,
    editingCardTitleValue,
    setEditingCardTitleValue,
    addingCardColumnId,
    newCardTitle,
    setNewCardTitle,
    handleColumnTitleClick,
    handleColumnTitleSubmit,
    handleAddCardClick,
    handleAddCardConfirm,
    handleAddCardCancel,
    handleOpenListMenu,
    handleOpenCardMenu,
    handleCardClick,
    handleUpdateCard,
    handleBoardCardTitleSubmit,
    handleBoardCardTitleCancel,
    handleAddComment,
    handleMoveCard,
    getBoardListsForMove,
    listMenuAnchor,
    listMenuView,
    setListMenuAnchor,
    setListMenuView,
    copyListName,
    setCopyListName,
    moveListPosition,
    setMoveListPosition,
    moveAllCardsTargetId,
    setMoveAllCardsTargetId,
    sortByOption,
    setSortByOption,
    handleListMenuAction,
    handleCopyListCreate,
    handleMoveListToPosition,
    handleMoveAllCardsToTarget,
    handleSortListBy,
    handleArchiveListWithConfirm,
    handleConfirmArchiveList,
    archiveListConfirmColumnId,
    setArchiveListConfirmColumnId,
    handleArchiveAllCardsInList,
    cardMenuAnchor,
    cardMenuView,
    setCardMenuAnchor,
    setCardMenuView,
    handleArchiveCard,
    showArchivedPanel,
    setShowArchivedPanel,
    showMembersPanel,
    setShowMembersPanel,
    archiveSearch,
    setArchiveSearch,
    archiveFilterType,
    setArchiveFilterType,
    archiveSort,
    setArchiveSort,
    archiveItemsGroupedByPeriod,
    filteredArchivedCards,
    filteredArchivedLists,
    handleRestoreCard,
    handleRestoreCardWithTarget,
    handleRestoreList,
    setDeleteConfirmItem,
    deleteConfirmItem,
    handleConfirmPermanentDelete,
    restoreCardItem,
    setRestoreCardItem,
    restoreTargetListId,
    setRestoreTargetListId,
    boardToDelete,
    setBoardToDelete,
    handleConfirmDeleteBoard,
    addMemberEmail,
    setAddMemberEmail,
    addingMember,
    memberToRemove,
    setMemberToRemove,
    handleLinkUserToBoard,
    handleConfirmRemoveBoardMember,
    toast,
    setToast,
    createBoardForPicker,
    createLabel,
    updateLabel,
    deleteLabel,
  } = board;

  const [backgroundUrl, setBackgroundUrl] = useState<string | null>(null);
  const [showBackgroundModal, setShowBackgroundModal] = useState(false);

  const DEFAULT_TITLE_COLOR = '#333333';
  const DEFAULT_SUBTITLE_COLOR = '#808080';
  const TEXT_COLORS_STORAGE_PREFIX = 'kanban-board-text-colors';

  const [showTextColorModal, setShowTextColorModal] = useState(false);
  const [titleTextColor, setTitleTextColor] = useState(DEFAULT_TITLE_COLOR);
  const [subtitleTextColor, setSubtitleTextColor] = useState(
    DEFAULT_SUBTITLE_COLOR,
  );

  const isValidHexColor = useCallback((value: unknown): value is string => {
    return (
      typeof value === 'string' && /^#([0-9a-fA-F]{6})$/.test(value.trim())
    );
  }, []);

  const listOverlayCardIds = useMemo(
    () =>
      activeListColumn ? Array.from(new Set(activeListColumn.cardIds)) : [],
    [activeListColumn],
  );

  const showBoardPicker = !companyId || !activeBoardId;

  useEffect(() => {
    if (!activeBoardId || !companyId) {
      setBackgroundUrl(null);
      return;
    }
    if (!activeBoard?.backgroundImagePath) {
      setBackgroundUrl(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const res = await getBoardBackgroundDownloadUrl(
          activeBoardId,
          companyId,
        );
        if (!cancelled) {
          setBackgroundUrl(res.downloadUrl ?? null);
        }
      } catch {
        if (!cancelled) {
          setBackgroundUrl(null);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [activeBoardId, companyId, activeBoard?.backgroundImagePath]);

  useEffect(() => {
    if (!activeBoardId) {
      setTitleTextColor(DEFAULT_TITLE_COLOR);
      setSubtitleTextColor(DEFAULT_SUBTITLE_COLOR);
      return;
    }
    const key = `${TEXT_COLORS_STORAGE_PREFIX}:${activeBoardId}`;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        setTitleTextColor(DEFAULT_TITLE_COLOR);
        setSubtitleTextColor(DEFAULT_SUBTITLE_COLOR);
        return;
      }
      const parsed: unknown = JSON.parse(raw);
      if (
        typeof parsed === 'object' &&
        parsed != null &&
        'titleColor' in parsed &&
        'subtitleColor' in parsed
      ) {
        const maybeTitle = (parsed as { titleColor?: unknown }).titleColor;
        const maybeSubtitle = (parsed as { subtitleColor?: unknown })
          .subtitleColor;
        setTitleTextColor(
          isValidHexColor(maybeTitle) ? maybeTitle : DEFAULT_TITLE_COLOR,
        );
        setSubtitleTextColor(
          isValidHexColor(maybeSubtitle)
            ? maybeSubtitle
            : DEFAULT_SUBTITLE_COLOR,
        );
      } else {
        setTitleTextColor(DEFAULT_TITLE_COLOR);
        setSubtitleTextColor(DEFAULT_SUBTITLE_COLOR);
      }
    } catch {
      setTitleTextColor(DEFAULT_TITLE_COLOR);
      setSubtitleTextColor(DEFAULT_SUBTITLE_COLOR);
    }
  }, [
    activeBoardId,
    DEFAULT_SUBTITLE_COLOR,
    DEFAULT_TITLE_COLOR,
    TEXT_COLORS_STORAGE_PREFIX,
    isValidHexColor,
  ]);

  const handleSaveTextColors = useCallback(
    (payload: { titleColor: string; subtitleColor: string }) => {
      if (!activeBoardId) return;
      const key = `${TEXT_COLORS_STORAGE_PREFIX}:${activeBoardId}`;
      setTitleTextColor(payload.titleColor);
      setSubtitleTextColor(payload.subtitleColor);
      try {
        localStorage.setItem(key, JSON.stringify(payload));
      } catch {
        // ignore storage failures
      }
      setShowTextColorModal(false);
    },
    [TEXT_COLORS_STORAGE_PREFIX, activeBoardId],
  );

  const handleResetTextColors = useCallback(() => {
    handleSaveTextColors({
      titleColor: DEFAULT_TITLE_COLOR,
      subtitleColor: DEFAULT_SUBTITLE_COLOR,
    });
  }, [DEFAULT_SUBTITLE_COLOR, DEFAULT_TITLE_COLOR, handleSaveTextColors]);

  return (
    <div
      className={styles.container}
      style={{
        ...(backgroundUrl
          ? {
              backgroundImage: `url(${backgroundUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }
          : {}),
        ['--kanban-board-title-color' as any]: titleTextColor,
        ['--kanban-board-subtitle-color' as any]: subtitleTextColor,
      }}
    >
      {showBoardPicker ? (
        <BoardPicker
          companyId={companyId}
          boards={boards}
          boardsLoading={boardsLoading}
          boardsError={boardsError}
          creatingBoard={creatingBoard}
          onSelectBoard={(id) => setActiveBoardId(id)}
          onCreateBoard={() => {
            void createBoardForPicker();
          }}
          onDeleteBoard={setBoardToDelete}
        />
      ) : null}
      {!showBoardPicker && boardDataError && (
        <div className={styles.boardDataError}>
          <p>{boardDataError}</p>
          <button
            type="button"
            className={styles.retryBtn}
            onClick={() => void loadBoardData(activeBoardId)}
          >
            Retry
          </button>
        </div>
      )}
      {!showBoardPicker && (
        <KanbanBoardHeader
          boardTitle={activeBoard?.title ?? 'Kanban'}
          editing={editingBoardTitle}
          titleDraft={boardTitleDraft}
          onBack={() => setActiveBoardId(null)}
          onTitleClick={handleBoardTitleClick}
          onTitleChange={setBoardTitleDraft}
          onTitleSubmit={() => void handleBoardTitleSubmit()}
          onTitleCancel={() => {
            setEditingBoardTitle(false);
            setBoardTitleDraft('');
          }}
          menuTriggerRef={boardMenuButtonRef}
          menuOpen={boardMenuOpen}
          onMenuToggle={() => setBoardMenuOpen((o) => !o)}
          onMenuClose={() => setBoardMenuOpen(false)}
          archiveCount={archiveCount}
          showDeleteBoard={Boolean(activeBoard)}
          onChangeBackground={() => setShowBackgroundModal(true)}
          onChangeTextColor={() => setShowTextColorModal(true)}
          onShowMembers={() => setShowMembersPanel(true)}
          onShowArchived={() => setShowArchivedPanel(true)}
          onDeleteBoard={() => activeBoard && setBoardToDelete(activeBoard)}
        />
      )}
      {!showBoardPicker && (
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={(e) => void handleDragEnd(e)}
          onDragCancel={handleDragCancel}
        >
          <div
            ref={boardWrapperRef}
            className={`${styles.boardWrapper} ${activeCard ? styles.boardWrapperCardDragging : ''}`}
            onMouseDown={handleBoardMouseDown}
            onMouseMove={handleBoardMouseMove}
            onMouseUp={handleBoardMouseUp}
            onMouseLeave={handleBoardMouseLeave}
            role="region"
            aria-label="Board"
          >
            <div className={styles.board}>
              <SortableContext
                items={columns.map((c) => c.id)}
                strategy={horizontalListSortingStrategy}
              >
                {columns.map((column) => (
                  <SortableListWrapper
                    key={column.id}
                    column={column}
                    cards={cards}
                    isEditingTitle={editingColumnId === column.id}
                    editingTitleValue={
                      editingColumnId === column.id ? editingColumnTitle : ''
                    }
                    onTitleClick={handleColumnTitleClick}
                    onTitleChange={setEditingColumnTitle}
                    onTitleSubmit={handleColumnTitleSubmit}
                    onAddCardClick={handleAddCardClick}
                    addingCardColumnId={addingCardColumnId}
                    newCardTitle={newCardTitle}
                    onNewCardTitleChange={setNewCardTitle}
                    onAddCardConfirm={handleAddCardConfirm}
                    onAddCardCancel={handleAddCardCancel}
                    onOpenListMenu={handleOpenListMenu}
                    onCardClick={handleCardClick}
                    onOpenCardMenu={handleOpenCardMenu}
                    boardLabels={boardLabels}
                    boardMembers={boardMembers}
                    editingCardTitleId={editingCardTitleId}
                    editingCardTitleValue={editingCardTitleValue}
                    onCardTitleChange={setEditingCardTitleValue}
                    onCardTitleSubmit={handleBoardCardTitleSubmit}
                    onCardTitleCancel={handleBoardCardTitleCancel}
                    isCardDragActive={Boolean(activeCard)}
                  />
                ))}
              </SortableContext>
              {addingColumn ? (
                <div className={styles.addColumnForm}>
                  <input
                    type="text"
                    className={styles.inputColumnTitle}
                    placeholder="List title"
                    value={newColumnTitle}
                    onChange={(e) => setNewColumnTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void handleAddColumnConfirm();
                      if (e.key === 'Escape') handleAddColumnCancel();
                    }}
                    autoFocus
                    aria-label="List title"
                  />
                  <div className={styles.formActions}>
                    <button
                      type="button"
                      className={`${styles.formBtn} ${styles.primary}`}
                      onClick={() => void handleAddColumnConfirm()}
                      disabled={!newColumnTitle.trim()}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      className={`${styles.formBtn} ${styles.secondary}`}
                      onClick={handleAddColumnCancel}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className={styles.addColumnBtn}
                  onClick={handleAddColumnClick}
                >
                  <Plus
                    size={18}
                    style={{ verticalAlign: 'middle', marginRight: 6 }}
                  />
                  Add list
                </button>
              )}
            </div>
          </div>
          <DragOverlay>
            {activeCard ? (
              <div className={styles.cardOverlayWrap}>
                <KanbanCardItem
                  card={activeCard}
                  isOverlay
                  onCardClick={() => {}}
                  onOpenCardMenu={() => {}}
                  currentColumnId=""
                  boardLabels={boardLabels}
                  boardMembers={boardMembers}
                />
              </div>
            ) : activeListColumn ? (
              <div className={styles.listOverlay}>
                <div className={styles.columnHeader}>
                  <div className={styles.columnTitleRow}>
                    <span>{activeListColumn.title}</span>
                    <span className={styles.columnCardCount}>
                      {listOverlayCardIds.length}
                    </span>
                  </div>
                </div>
                <div className={styles.listOverlayBody}>
                  {listOverlayCardIds.map((cardId) => {
                    const card = cards[cardId];
                    if (!card) return null;
                    return (
                      <div key={card.id} className={styles.listOverlayCard}>
                        <span className={styles.listOverlayCardTitle}>
                          {card.title}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
      <CardDetailModal
        show={Boolean(selectedCardId)}
        onHide={() => {
          setSelectedCardId(null);
          setRenameCardId(null);
        }}
        card={selectedCard}
        columnTitle={selectedColumnTitle}
        boardId={activeBoardId}
        activeBoardId={activeBoardId}
        boards={boards}
        commentIdToScroll={commentIdToScroll}
        onCopyCommentLink={onCopyCommentLink}
        companyId={companyId}
        boardLabels={boardLabels}
        boardMembers={boardMembers}
        columns={columns}
        onSelectCard={setSelectedCardId}
        onUpdateCard={(id, updates) => void handleUpdateCard(id, updates)}
        onAddComment={(id, text, attachments) =>
          void handleAddComment(id, text, attachments)
        }
        renameCardId={renameCardId}
        onClearRenameRequest={() => setRenameCardId(null)}
        onCreateLabel={async (label) => {
          if (!activeBoardId || !companyId) return null;
          return createLabel(
            activeBoardId,
            { name: label.name, color: label.color },
            companyId,
          );
        }}
        onUpdateLabel={async (labelId, updates) => {
          if (!activeBoardId || !companyId) return null;
          return updateLabel(activeBoardId, labelId, updates, companyId);
        }}
        onDeleteLabel={async (labelId) => {
          if (!activeBoardId || !companyId) return;
          await deleteLabel(activeBoardId, labelId, companyId);
        }}
        onCopyCardLink={onCopyCardLink}
        onArchiveCard={(cardId) => void handleArchiveCard(cardId)}
        onMoveCard={(
          cardId,
          targetColumnId,
          targetBoardId,
          targetPosition0Based,
        ) =>
          void handleMoveCard(
            cardId,
            targetColumnId,
            targetBoardId,
            targetPosition0Based,
          )
        }
        getBoardListsForMove={getBoardListsForMove}
        onCommentUploadError={(message) => setToast({ message, type: 'error' })}
      />

      <ListMenu
        anchor={listMenuAnchor}
        view={listMenuView}
        columns={columns}
        copyListName={copyListName}
        moveListPosition={moveListPosition}
        moveAllCardsTargetId={moveAllCardsTargetId}
        sortByOption={sortByOption}
        onClose={() => {
          setListMenuAnchor(null);
          setListMenuView('main');
        }}
        onViewChange={setListMenuView}
        onRenameColumn={(columnId) => {
          const col = columns.find((c) => c.id === columnId);
          if (col) {
            setEditingColumnTitle(col.title);
            setEditingColumnId(columnId);
          }
          setListMenuAnchor(null);
        }}
        onListMenuAction={handleListMenuAction}
        onCopyListCreate={() => void handleCopyListCreate()}
        onCopyListNameChange={setCopyListName}
        onMoveListToPosition={() => void handleMoveListToPosition()}
        onMoveListPositionChange={setMoveListPosition}
        onMoveAllCardsToTarget={() => void handleMoveAllCardsToTarget()}
        onMoveAllCardsTargetIdChange={setMoveAllCardsTargetId}
        onSortListBy={() => void handleSortListBy()}
        onSortByOptionChange={setSortByOption}
        onArchiveList={handleArchiveListWithConfirm}
        onArchiveAllCards={(columnId) =>
          void handleArchiveAllCardsInList(columnId)
        }
      />

      <CardMenu
        anchor={cardMenuAnchor}
        activeBoardId={activeBoardId}
        boards={boards}
        view={cardMenuView}
        columns={columns}
        getBoardListsForMove={getBoardListsForMove}
        onClose={() => {
          setCardMenuAnchor(null);
          setCardMenuView('main');
        }}
        onViewChange={setCardMenuView}
        onRenameCard={(cardId) => {
          const c = cards[cardId];
          if (c) {
            setEditingCardTitleValue(c.title);
            setEditingCardTitleId(cardId);
          }
          setCardMenuAnchor(null);
        }}
        onArchiveCard={(cardId) => {
          void handleArchiveCard(cardId);
          setCardMenuAnchor(null);
        }}
        onMoveCard={(
          cardId,
          targetColumnId,
          targetBoardId,
          targetPosition0Based,
        ) => {
          void handleMoveCard(
            cardId,
            targetColumnId,
            targetBoardId,
            targetPosition0Based,
          );
          setCardMenuAnchor(null);
        }}
        onCopyCardLink={onCopyCardLink}
      />

      <ArchivePanel
        show={showArchivedPanel}
        onClose={() => setShowArchivedPanel(false)}
        archiveSearch={archiveSearch}
        onArchiveSearchChange={setArchiveSearch}
        archiveFilterType={archiveFilterType}
        onArchiveFilterTypeChange={setArchiveFilterType}
        archiveSort={archiveSort}
        onArchiveSortChange={setArchiveSort}
        archiveItemsGroupedByPeriod={archiveItemsGroupedByPeriod}
        filteredArchivedCards={filteredArchivedCards}
        filteredArchivedLists={filteredArchivedLists}
        onRestoreCard={(a) => void handleRestoreCard(a)}
        onRestoreList={(a) => void handleRestoreList(a)}
        onRequestDeleteCard={(a) =>
          setDeleteConfirmItem({ type: 'card', item: a })
        }
        onRequestDeleteList={(a) =>
          setDeleteConfirmItem({ type: 'list', item: a })
        }
      />

      <BoardMembersPanel
        show={showMembersPanel}
        onClose={() => setShowMembersPanel(false)}
        boardMembers={boardMembers}
        addMemberEmail={addMemberEmail}
        onAddMemberEmailChange={setAddMemberEmail}
        addingMember={addingMember}
        onLinkUser={(email) => void handleLinkUserToBoard(email)}
        onRequestRemoveMember={setMemberToRemove}
      />

      {memberToRemove && (
        <KanbanConfirmModal
          show
          onHide={() => setMemberToRemove(null)}
          title="Remove member?"
          confirmLabel="Remove from board"
          onConfirm={() => void handleConfirmRemoveBoardMember()}
          variant="danger"
        >
          <p>
            Remove &quot;{memberToRemove.name}&quot; from this board? They will
            no longer appear in the members list or on cards.
          </p>
        </KanbanConfirmModal>
      )}

      {restoreCardItem && (
        <KanbanConfirmModal
          show
          onHide={() => setRestoreCardItem(null)}
          title="Restore card"
          confirmLabel="Restore"
          onConfirm={() => {
            if (restoreTargetListId && restoreCardItem)
              void handleRestoreCardWithTarget(
                restoreCardItem,
                restoreTargetListId,
              );
          }}
          confirmDisabled={!restoreTargetListId}
        >
          <p>
            Original list &quot;{restoreCardItem.originalListTitle}&quot; no
            longer exists. Choose where to restore &quot;
            {restoreCardItem.card.title}&quot;:
          </p>
          <label className={styles.sidePanelLabel}>Target list</label>
          <select
            className={styles.sidePanelSelect}
            value={restoreTargetListId ?? ''}
            onChange={(e) => setRestoreTargetListId(e.target.value || null)}
            aria-label="Target list"
          >
            <option value="">Select list</option>
            {columns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
        </KanbanConfirmModal>
      )}

      {archiveListConfirmColumnId && (
        <KanbanConfirmModal
          show
          onHide={() => setArchiveListConfirmColumnId(null)}
          title="Archive list?"
          confirmLabel="Archive list"
          onConfirm={() => void handleConfirmArchiveList()}
        >
          <p>
            This will move the list and all its cards to Archived Items. You can
            restore them later from the board menu.
          </p>
        </KanbanConfirmModal>
      )}

      {deleteConfirmItem && (
        <KanbanConfirmModal
          show
          onHide={() => setDeleteConfirmItem(null)}
          title="Permanently delete?"
          confirmLabel="Delete permanently"
          onConfirm={() => void handleConfirmPermanentDelete()}
          variant="danger"
        >
          <p>
            This will permanently delete this{' '}
            {deleteConfirmItem.type === 'card' ? 'card' : 'list'}
            {deleteConfirmItem.type === 'card'
              ? ` "${deleteConfirmItem.item.card.title}"`
              : ` "${deleteConfirmItem.item.column.title}"`}
            . This action cannot be undone.
          </p>
        </KanbanConfirmModal>
      )}

      {!showBoardPicker && activeBoardId && (
        <BoardBackgroundModal
          show={showBackgroundModal}
          onHide={() => setShowBackgroundModal(false)}
          boardId={activeBoardId}
          companyId={companyId}
          hasBackground={Boolean(activeBoard?.backgroundImagePath)}
          onBackgroundUpdated={({ downloadUrl }) => {
            setBackgroundUrl(downloadUrl);
          }}
        />
      )}

      {boardToDelete && (
        <KanbanConfirmModal
          show
          onHide={() => setBoardToDelete(null)}
          title="Delete board?"
          confirmLabel="Delete board"
          onConfirm={() => void handleConfirmDeleteBoard()}
          variant="danger"
        >
          <p>
            This will permanently delete the board &quot;{boardToDelete.title}
            &quot; and all its lists and cards. This action cannot be undone.
          </p>
        </KanbanConfirmModal>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <BoardTextColorModal
        show={showTextColorModal}
        onHide={() => setShowTextColorModal(false)}
        initialTitleColor={titleTextColor}
        initialSubtitleColor={subtitleTextColor}
        onSave={handleSaveTextColors}
        onResetToDefault={handleResetTextColors}
      />
    </div>
  );
}

export default Kanban;
